// eslint-disable-next-line no-restricted-imports -- DO NOT COPY THIS LINE. See docs/lints.md.
import { Stat } from "@suns/api/generated-client/apollo";
import { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";

export const gameColumns: ColumnDef<Stat>[] = [
  {
    header: "",
    accessorKey: "gameTimeUtc",
    accessorFn: (statRow) =>
      `${dayjs(statRow?.gameTimeUtc, "YYYY-MM-DDTHH:mm:ssZ").format(
        "MMM DD, YYYY"
      )} - ${statRow?.opponentTeamName}`,
    enableSorting: false,
  },
  {
    header: "W/L",
    accessorKey: "wl",
    enableSorting: false,
  },
];

export const seasonColumns: ColumnDef<Stat>[] = [
  {
    header: "",
    accessorKey: "season",
    accessorFn: (statRow) =>
      statRow?.season
        ? `${statRow?.leagueName} ${statRow?.season}${statRow?.seasonType !== "Regular Season" ? ` - ${statRow?.seasonType}` : ""}`
        : "Career",
    enableSorting: false,
  },
  {
    header: "GP",
    accessorKey: "gp",
    enableSorting: false,
  },
];

type StatColumnDef<T extends "boxscore" | "advanced"> = Omit<
  ColumnDef<Stat>,
  "accessorKey"
> & {
  accessorKey: `${T}.${Extract<keyof Exclude<Stat[T], null>, string>}`;
};

export const boxscoreColumns = (
  digits: number
): StatColumnDef<"boxscore">[] => [
  {
    header: "MIN",
    accessorKey: "boxscore.min",
    enableSorting: false,
    cell: (props) => Number(props.getValue()).toFixed(digits),
  },
  {
    header: "PTS",
    accessorKey: "boxscore.pts",
    enableSorting: false,
    cell: (props) => Number(props.getValue()).toFixed(digits),
  },
  {
    header: "FGM",
    accessorKey: "boxscore.fgm",
    enableSorting: false,
    cell: (props) => Number(props.getValue()).toFixed(digits),
  },
  {
    header: "FGA",
    accessorKey: "boxscore.fga",
    enableSorting: false,
    cell: (props) => Number(props.getValue()).toFixed(digits),
  },
  {
    header: "FG%",
    accessorKey: "boxscore.fgPct",
    enableSorting: false,
    cell: (props) => `${Number(props.getValue()).toFixed(digits)}%`,
  },
  {
    header: "3PM",
    accessorKey: "boxscore.fg3m",
    enableSorting: false,
    cell: (props) => Number(props.getValue()).toFixed(digits),
  },
  {
    header: "3PA",
    accessorKey: "boxscore.fg3a",
    enableSorting: false,
    cell: (props) => Number(props.getValue()).toFixed(digits),
  },
  {
    header: "3P%",
    accessorKey: "boxscore.fg3Pct",
    enableSorting: false,
    cell: (props) => `${Number(props.getValue()).toFixed(digits)}%`,
  },
  {
    header: "FTM",
    accessorKey: "boxscore.ftm",
    enableSorting: false,
    cell: (props) => Number(props.getValue()).toFixed(digits),
  },
  {
    header: "FTA",
    accessorKey: "boxscore.fta",
    enableSorting: false,
    cell: (props) => Number(props.getValue()).toFixed(digits),
  },
  {
    header: "FT%",
    accessorKey: "boxscore.ftPct",
    enableSorting: false,
    cell: (props) => `${Number(props.getValue()).toFixed(digits)}%`,
  },
  {
    header: "ORB",
    accessorKey: "boxscore.oreb",
    enableSorting: false,
    cell: (props) => Number(props.getValue()).toFixed(digits),
  },
  {
    header: "DRB",
    accessorKey: "boxscore.dreb",
    enableSorting: false,
    cell: (props) => Number(props.getValue()).toFixed(digits),
  },
  {
    header: "REB",
    accessorKey: "boxscore.reb",
    enableSorting: false,
    cell: (props) => Number(props.getValue()).toFixed(digits),
  },
  {
    header: "AST",
    accessorKey: "boxscore.ast",
    enableSorting: false,
    cell: (props) => Number(props.getValue()).toFixed(digits),
  },
  {
    header: "TOV",
    accessorKey: "boxscore.tov",
    enableSorting: false,
    cell: (props) => Number(props.getValue()).toFixed(digits),
  },
  {
    header: "STL",
    accessorKey: "boxscore.stl",
    enableSorting: false,
    cell: (props) => Number(props.getValue()).toFixed(digits),
  },
  {
    header: "BLK",
    accessorKey: "boxscore.blk",
    enableSorting: false,
    cell: (props) => Number(props.getValue()).toFixed(digits),
  },
  {
    header: "PF",
    accessorKey: "boxscore.pf",
    enableSorting: false,
    cell: (props) => Number(props.getValue()).toFixed(digits),
  },
  {
    header: "+/-",
    accessorKey: "boxscore.plusMinus",
    enableSorting: false,
    cell: (props) =>
      `${Number(props.getValue()) > 0 ? "+" : ""}${Number(props.getValue()).toFixed(digits)}`,
  },
];

export const advancedColumns = (
  digits: number
): StatColumnDef<"advanced">[] => [
  {
    header: "OFF RTG",
    accessorKey: "advanced.offRating",
    enableSorting: false,
    cell: (props) => Number(props.getValue()).toFixed(digits),
  },
  {
    header: "DEF RTG",
    accessorKey: "advanced.defRating",
    enableSorting: false,
    cell: (props) => Number(props.getValue()).toFixed(digits),
  },
  {
    header: "NET RTG",
    accessorKey: "advanced.netRating",
    enableSorting: false,
    cell: (props) => Number(props.getValue()).toFixed(digits),
  },
  {
    header: "AST%",
    accessorKey: "advanced.astPct",
    enableSorting: false,
    cell: (props) => `${Number(props.getValue()).toFixed(digits)}%`,
  },
  {
    header: "AST/TO",
    accessorKey: "advanced.astTo",
    enableSorting: false,
    cell: (props) => Number(props.getValue()).toFixed(digits),
  },
  {
    header: "OREB%",
    accessorKey: "advanced.orebPct",
    enableSorting: false,
    cell: (props) => `${Number(props.getValue()).toFixed(digits)}%`,
  },
  {
    header: "DREB%",
    accessorKey: "advanced.drebPct",
    enableSorting: false,
    cell: (props) => `${Number(props.getValue()).toFixed(digits)}%`,
  },
  {
    header: "REB%",
    accessorKey: "advanced.rebPct",
    enableSorting: false,
    cell: (props) => `${Number(props.getValue()).toFixed(digits)}%`,
  },
  {
    header: "TOV%",
    accessorKey: "advanced.tovPct",
    enableSorting: false,
    cell: (props) => `${Number(props.getValue()).toFixed(digits)}%`,
  },
  {
    header: "eFG%",
    accessorKey: "advanced.efgPct",
    enableSorting: false,
    cell: (props) => `${Number(props.getValue()).toFixed(digits)}%`,
  },
  {
    header: "TS%",
    accessorKey: "advanced.tsPct",
    enableSorting: false,
    cell: (props) => `${Number(props.getValue()).toFixed(digits)}%`,
  },
  {
    header: "USG%",
    accessorKey: "advanced.usgPct",
    enableSorting: false,
    cell: (props) => `${Number(props.getValue()).toFixed(digits)}%`,
  },
  {
    header: "STL%",
    accessorKey: "advanced.stlPct",
    enableSorting: false,
    cell: (props) => `${Number(props.getValue()).toFixed(digits)}%`,
  },
  {
    header: "BLK%",
    accessorKey: "advanced.blkPct",
    enableSorting: false,
    cell: (props) => `${Number(props.getValue()).toFixed(digits)}%`,
  },
  {
    header: "PACE",
    accessorKey: "advanced.pace",
    enableSorting: false,
    cell: (props) => Number(props.getValue()).toFixed(digits),
  },
];
